import React, { createContext, FC, useCallback } from 'react';
import { useLocation } from 'react-router';
import { noop } from 'rxjs';
import { ModalName } from '../../../constants/ModalName';
import { MainSource } from '../../../constants/Source';
import { SystemFolderId } from '../../../constants/SystemFolderId';
import {
  purchaseFailedNotification,
  purchaseSucceedNotification,
} from '../../../containers/FilesManager/notifications';
import { transferringPurchasedFileNotification } from '../../../containers/FilesManager/notifications/transferringPurchasedFileNotification';
import { calculateTabName } from '../../bi';
import { ItemsSubmitType } from '../../hoc/bi/withItemsSubmittedAnalytics/withItemsSubmittedAnalytics.types';
import { useMMGRSettings } from '../../hoc/withMMGRSettings';
import { MediaManagerSDKFile } from '../../media-manager-sdk';
import { isShutterstock as isShutterstockSource } from '../../media-manager-sdk/sources';
import { buyItem } from '../../paidItem';
import { buyItemNewCheckout } from '../../paidItemNewCheckout';
import { generatePurchaseTab } from '../../paidItemNewCheckout/utils';
import { useTrackItemsSubmittedAnalytics } from '../bi/useTrackItemsSubmittedAnalytics';
import { useTrackPurchaseAnalytics } from '../bi/useTrackPurchaseAnalytics';
import { useModalHandlersAndOptions } from '../useModal';
import { useNotificatorConsumer } from '../useNotificator';
import {
  IPurchaseItemFactoryParams,
  IUsePurchaseMediaContext,
  THandleSuccessfulPurchase,
} from './usePurchaseMedia.types';

export const PurchaseMediaContext = createContext<IUsePurchaseMediaContext>({
  purchaseItemFunctionFactory:
    (
      _params = {
        source: null,
        category: null,
        categoryId: null,
        searchTerm: null,
        orientation: null,
        openFolderById: noop,
      },
    ) =>
    (_item) =>
      Promise.resolve(),
});

export const PurchaseMediaProvider: FC = ({ children }) => {
  const { mmgrSettings } = useMMGRSettings();
  const { notify } = useNotificatorConsumer();
  const { pathname } = useLocation();
  const { openModal: openShutterstockDisabledModal } = useModalHandlersAndOptions(ModalName.ShutterstockDisabled);
  const { trackMediaManagerFileChosen } = useTrackItemsSubmittedAnalytics();
  const { trackMediaManagerMediaPurchaseCompleted } = useTrackPurchaseAnalytics();

  const openPurchasedFolder = useCallback((openFolderById) => {
    openFolderById(SystemFolderId.Purchased, MainSource.SiteFiles, '');
  }, []);

  const handleSuccessfulPurchase = useCallback<THandleSuccessfulPurchase>(
    ({ item, category, source, openFolderById, isNew }) => {
      const tabName = calculateTabName({ pathname, source });

      trackMediaManagerMediaPurchaseCompleted({ item, isNew, category, tabName });

      const notification = isNew
        ? purchaseSucceedNotification({ item })
        : transferringPurchasedFileNotification({ item });

      notify(notification);
      openPurchasedFolder(openFolderById);
    },
    [pathname, trackMediaManagerMediaPurchaseCompleted, notify, openPurchasedFolder],
  );

  const handleFailPurchase = useCallback(
    (item) => {
      notify(purchaseFailedNotification({ item }));
    },
    [notify],
  );

  const purchaseItemFunctionFactory = useCallback(
    ({ source, category, categoryId, searchTerm, orientation, openFolderById }: IPurchaseItemFactoryParams) =>
      async (itemToPurchase: MediaManagerSDKFile) => {
        const { isCantBuyOnShutterstockExperimentEnabled, isNewCheckoutExperimentEnabled } = mmgrSettings;

        trackMediaManagerFileChosen({
          items: [itemToPurchase],
          target: ItemsSubmitType.Buy,
          category,
          categoryId,
          searchTerm,
          orientation,
        });

        if (isCantBuyOnShutterstockExperimentEnabled && isShutterstockSource(itemToPurchase.source)) {
          openShutterstockDisabledModal();
          return;
        }

        try {
          const { isNew } = await (isNewCheckoutExperimentEnabled
            ? buyItemNewCheckout(itemToPurchase, mmgrSettings, generatePurchaseTab(source))
            : buyItem(itemToPurchase, mmgrSettings));

          handleSuccessfulPurchase({ item: itemToPurchase, isNew, source, category, openFolderById });
        } catch (e) {
          handleFailPurchase(itemToPurchase);
        }
      },
    [mmgrSettings, trackMediaManagerFileChosen, handleSuccessfulPurchase, handleFailPurchase],
  );

  return (
    <PurchaseMediaContext.Provider
      value={{
        purchaseItemFunctionFactory,
      }}
    >
      {children}
    </PurchaseMediaContext.Provider>
  );
};
